import React, { useEffect, useState, Component } from 'react';
import { XIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
import { TextField, DisplayField } from '../FormComponents';
interface SupplierViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier: any;
  type: 'EOI' | 'RFP' | 'RFQ';
  onSaveScores?: (id: string, technicalScore: number, financialScore: number) => void;
}
export function SupplierViewModal({
  isOpen,
  onClose,
  supplier,
  type,
  onSaveScores
}: SupplierViewModalProps) {
  const [technicalScore, setTechnicalScore] = useState<number | ''>('');
  const [financialScore, setFinancialScore] = useState<number | ''>('');
  const isRFPOrRFQ = type === 'RFP' || type === 'RFQ';
  useEffect(() => {
    if (supplier) {
      setTechnicalScore(supplier.technicalScore || '');
      setFinancialScore(supplier.financialScore || '');
    }
  }, [supplier]);
  if (!isOpen || !supplier) return null;
  const handleSave = () => {
    if (onSaveScores && isRFPOrRFQ) {
      onSaveScores(supplier.id, Number(technicalScore) || 0, Number(financialScore) || 0);
    }
    onClose();
  };
  const totalScore = isRFPOrRFQ ? (Number(technicalScore) || 0) + (Number(financialScore) || 0) : 0;
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-448">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0" data-id="element-449">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} data-id="element-450"></div>

        <div className="relative inline-block w-full max-w-2xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6" data-id="element-451">
          <div className="absolute top-0 right-0 pt-4 pr-4" data-id="element-452">
            <button type="button" className="text-gray-400 bg-white rounded-md hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2" onClick={onClose} data-id="element-453">
              <span className="sr-only" data-id="element-454">Close</span>
              <XIcon className="w-6 h-6" aria-hidden="true" data-id="element-455" />
            </button>
          </div>

          <div className="sm:flex sm:items-start" data-id="element-456">
            <div className="w-full mt-3 text-center sm:mt-0 sm:text-left" data-id="element-457">
              <h3 className="text-lg font-medium leading-6 text-gray-900 border-b border-gray-200 pb-4 mb-4" data-id="element-458">
                Supplier Details: {supplier.supplierName}
              </h3>

              <div className="space-y-6" data-id="element-459">
                {/* General Info */}
                <div className="grid grid-cols-2 gap-4" data-id="element-460">
                  <DisplayField label="Supplier Name" value={supplier.supplierName} data-id="element-461" />
                  <DisplayField label={type === 'EOI' ? 'Submission Date' : 'Invitation Date'} value={type === 'EOI' ? supplier.submissionDate : supplier.invitationDate} data-id="element-462" />
                  {isRFPOrRFQ && <>
                      <DisplayField label="Submission Status" value={supplier.submissionStatus} data-id="element-463" />
                      <DisplayField label="Quoted Amount" value={supplier.quotedAmount ? `$${supplier.quotedAmount.toLocaleString()}` : '-'} data-id="element-464" />
                      <DisplayField label="Delivery Timeline" value={supplier.deliveryTimeline || '-'} data-id="element-465" />
                    </>}
                </div>

                {/* Documents */}
                <div data-id="element-466">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-467">
                    Uploaded Documents
                  </h4>
                  <ul className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-468">
                    {supplier.documents && supplier.documents.length > 0 ? supplier.documents.map((doc: string, index: number) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-469">
                          <div className="w-0 flex-1 flex items-center" data-id="element-470">
                            <FileTextIcon className="flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-471" />
                            <span className="ml-2 flex-1 w-0 truncate" data-id="element-472">
                              {doc}
                            </span>
                          </div>
                          <div className="ml-4 flex-shrink-0" data-id="element-473">
                            <button type="button" className="font-medium text-indigo-600 hover:text-indigo-500 flex items-center" data-id="element-474">
                              <DownloadIcon className="h-4 w-4 mr-1" data-id="element-475" />
                              Download
                            </button>
                          </div>
                        </li>) : <li className="pl-3 pr-4 py-3 text-sm text-gray-500" data-id="element-476">
                        No documents uploaded.
                      </li>}
                  </ul>
                </div>

                {/* Scoring (RFP and RFQ) */}
                {isRFPOrRFQ && <div className="bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-477">
                    <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-478">
                      Evaluation Scoring
                    </h4>
                    <div className="grid grid-cols-2 gap-4" data-id="element-479">
                      <TextField label="Technical Scoring" type="number" min="0" max="100" value={technicalScore} onChange={e => setTechnicalScore(e.target.value)} placeholder="Enter score" data-id="element-480" />
                      <TextField label="Financial Scoring" type="number" min="0" max="100" value={financialScore} onChange={e => setFinancialScore(e.target.value)} placeholder="Enter score" data-id="element-481" />
                      <div className="col-span-2 pt-2 border-t border-gray-200 flex justify-between items-center" data-id="element-482">
                        <span className="text-sm font-medium text-gray-700" data-id="element-483">
                          Total Score:
                        </span>
                        <span className="text-lg font-bold text-indigo-600" data-id="element-484">
                          {totalScore}
                        </span>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse" data-id="element-485">
            {isRFPOrRFQ && <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={handleSave} data-id="element-486">
                Save Scores
              </button>}
            <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm" onClick={onClose} data-id="element-487">
              {isRFPOrRFQ ? 'Cancel' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}